import { newUuid, type Hex } from '@/store/ids';
import {
  WorkerCallError,
  type WorkerCallName,
  type WorkerCallRequest,
  type WorkerCallResponse,
} from '@/transport/workerCalls';
import type {
  ConnectionPhase,
  PageToWorker,
  ResolvedUser,
  WorkerAccount,
  WorkerToPage,
} from '@/transport/workerProtocol';

/**
 * The page's handle on the sync worker.
 *
 * It owns the only `Worker` instance and the only place the page answers a
 * ticket request. Everything else in the interface talks to LocalStore; the
 * few things that genuinely need the network — asking for an older history
 * page, resolving a username — go through here.
 */

export interface HistoryState {
  hasMore: boolean;
  oldestSeq: number | null;
  loading: boolean;
  error: string | null;
}

type PhaseListener = (phase: ConnectionPhase) => void;
type HistoryListener = (conversationId: Hex, state: HistoryState) => void;
/**
 * Somebody started or stopped typing.
 *
 * The worker has posted these since typing was implemented and nothing on the
 * page consumed them, so the indicator worked end to end on the server and was
 * invisible in the interface. An expiry rather than a boolean: typing is true for
 * a few seconds and a stop notification can be lost, so the subscriber expires it
 * locally instead of waiting to be told.
 */
type TypingListener = (conversationId: Hex, userId: Hex, expiresAt: number) => void;

export interface WorkerClientOptions {
  /** Mints a single-use connection ticket over authenticated HTTPS. */
  mintTicket: () => Promise<string>;
  /** Credentials failed; the page must refresh the session or sign out. */
  onUnauthenticated: (reason: string) => void;
}

export class WorkerClient {
  private readonly worker: Worker;
  private readonly options: WorkerClientOptions;
  private readonly phaseListeners = new Set<PhaseListener>();
  private readonly historyListeners = new Set<HistoryListener>();
  private readonly typingListeners = new Set<TypingListener>();
  private readonly pendingResolves = new Map<
    string,
    { resolve: (user: ResolvedUser | null) => void; reject: (error: Error) => void }
  >();
  private readonly pendingCalls = new Map<
    string,
    { resolve: (value: never) => void; reject: (error: Error) => void }
  >();
  private readonly historyLoading = new Set<Hex>();
  private phase: ConnectionPhase = 'idle';
  private leader = false;

  constructor(options: WorkerClientOptions) {
    this.options = options;
    // A relative specifier is required here: Vite resolves the worker entry at
    // build time from this literal, and a path alias inside `new URL` is not
    // something its static analysis can follow.
    this.worker = new Worker(new URL('../worker/sync.worker.ts', import.meta.url), {
      type: 'module',
    });
    this.worker.onmessage = (event: MessageEvent<WorkerToPage>) => {
      void this.receive(event.data);
    };
  }

  get currentPhase(): ConnectionPhase {
    return this.phase;
  }

  get isLeader(): boolean {
    return this.leader;
  }

  start(wssUrl: string, account: WorkerAccount): void {
    this.send({ type: 'start', wssUrl, account });
  }

  stop(): void {
    this.send({ type: 'stop' });
  }

  terminate(): void {
    this.worker.terminate();
    // A terminated worker will never answer, so every waiting caller is told
    // now rather than being left with a promise that can only hang.
    const pending = [...this.pendingCalls.values()];
    this.pendingCalls.clear();
    for (const entry of pending) {
      entry.reject(new WorkerCallError('TRANSPORT', 'the sync worker stopped'));
    }
  }

  onPhase(listener: PhaseListener): () => void {
    this.phaseListeners.add(listener);
    listener(this.phase);
    return () => {
      this.phaseListeners.delete(listener);
    };
  }

  onTyping(listener: TypingListener): () => void {
    this.typingListeners.add(listener);
    return () => {
      this.typingListeners.delete(listener);
    };
  }

  onHistory(listener: HistoryListener): () => void {
    this.historyListeners.add(listener);
    return () => {
      this.historyListeners.delete(listener);
    };
  }

  /** Tells the worker new outbox work exists. Cheap and safe to over-call. */
  notifyOutboxChanged(): void {
    this.send({ type: 'outbox-changed' });
  }

  /**
   * CR-01 §5: asks the controller to accelerate one round.
   *
   * Safe to over-call. The immediate-retry budget allows one accelerated round
   * per rolling five seconds, so repeated presses collapse into one attempt
   * rather than producing a retry storm.
   */
  retryNow(): void {
    this.send({ type: 'retry-now' });
  }

  setActiveConversation(conversationId: Hex | null): void {
    this.send({ type: 'active-conversation', conversationId });
  }

  /**
   * Requests a history page.
   *
   * The in-flight guard is here rather than in the worker because it is a
   * user-interface concern: the timeline fires this from a scroll handler, and
   * without the guard a single flick past the threshold would queue a dozen
   * identical pages.
   */
  loadHistory(conversationId: Hex, older: boolean): void {
    if (this.historyLoading.has(conversationId)) {
      return;
    }
    this.historyLoading.add(conversationId);
    this.emitHistory(conversationId, { hasMore: true, oldestSeq: null, loading: true, error: null });
    this.send({ type: 'load-history', conversationId, older });
  }

  resolveUser(username: string | null, phoneE164: string | null): Promise<ResolvedUser | null> {
    const requestId = newUuid();
    return new Promise<ResolvedUser | null>((resolve, reject) => {
      this.pendingResolves.set(requestId, { resolve, reject });
      this.send({ type: 'resolve-user', requestId, username, phoneE164 });
    });
  }

  /**
   * Makes one typed call into the worker.
   *
   * There is deliberately no timeout here. The worker's own transport already
   * fails a request after thirty seconds and answers with an error, and a
   * second timeout on this side would reject a call the worker later answers,
   * leaving a resolved promise with no listener and a screen that says a query
   * failed when it succeeded.
   */
  call<K extends WorkerCallName>(
    name: K,
    request: WorkerCallRequest<K>,
  ): Promise<WorkerCallResponse<K>> {
    const requestId = newUuid();
    return new Promise<WorkerCallResponse<K>>((resolve, reject) => {
      this.pendingCalls.set(requestId, {
        resolve: resolve as (value: never) => void,
        reject,
      });
      this.send({ type: 'call', requestId, name, request } as PageToWorker);
    });
  }

  private send(message: PageToWorker): void {
    this.worker.postMessage(message);
  }

  private emitHistory(conversationId: Hex, state: HistoryState): void {
    for (const listener of this.historyListeners) {
      listener(conversationId, state);
    }
  }

  private async receive(message: WorkerToPage): Promise<void> {
    switch (message.type) {
      case 'phase':
        this.phase = message.phase;
        for (const listener of this.phaseListeners) {
          listener(message.phase);
        }
        return;

      case 'ticket-request': {
        // The page holds the access token and the cookie jar, so it — and only
        // it — can mint a ticket. A failure is reported rather than retried
        // here: the worker's own reconnect backoff is the right place for that.
        try {
          const ticket = await this.options.mintTicket();
          this.send({ type: 'ticket', requestId: message.requestId, ticket, error: null });
        } catch (error) {
          this.send({
            type: 'ticket',
            requestId: message.requestId,
            ticket: null,
            error: String(error),
          });
        }
        return;
      }

      case 'typing':
        for (const listener of this.typingListeners) {
          listener(message.conversationId, message.userId, message.expiresAt);
        }
        return;

      case 'unauthenticated':
        this.options.onUnauthenticated(message.reason);
        return;

      case 'leadership':
        this.leader = message.leader;
        return;

      case 'history-loaded':
        this.historyLoading.delete(message.conversationId);
        this.emitHistory(message.conversationId, {
          hasMore: message.hasMore,
          oldestSeq: message.oldestSeq,
          loading: false,
          error: null,
        });
        return;

      case 'history-failed':
        this.historyLoading.delete(message.conversationId);
        this.emitHistory(message.conversationId, {
          hasMore: true,
          oldestSeq: null,
          loading: false,
          error: message.code,
        });
        return;

      case 'resolve-user-result': {
        const pending = this.pendingResolves.get(message.requestId);
        if (!pending) {
          return;
        }
        this.pendingResolves.delete(message.requestId);
        pending.resolve(message.user);
        return;
      }

      case 'call-result': {
        const pending = this.pendingCalls.get(message.requestId);
        if (!pending) {
          return;
        }
        this.pendingCalls.delete(message.requestId);
        if (message.ok) {
          pending.resolve(message.response as never);
        } else {
          pending.reject(new WorkerCallError(message.code, message.message));
        }
        return;
      }

      case 'log':
        if (message.level === 'error') {
          console.error('[sync]', message.message);
        } else if (message.level === 'warn') {
          console.warn('[sync]', message.message);
        } else {
          console.info('[sync]', message.message);
        }
        return;
    }
  }
}
